import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Package,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  Phone,
  MapPin,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar,
  CreditCard,
  Wallet,
} from "lucide-react";

const ESTADOS = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700", icon: Clock },
  pagado_pendiente_entrega: { label: "Pagado - Por entregar", color: "bg-green-100 text-green-700", icon: CheckCircle },
  confirmado: { label: "Confirmado", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  en_transito: { label: "En Tránsito", color: "bg-purple-100 text-purple-700", icon: Truck },
  entregado: { label: "Entregado", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function VendedorPedidos() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [expandedPedido, setExpandedPedido] = useState(null);
  
  // Modal de actualizar estado
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [notasActualizacion, setNotasActualizacion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/pedidos-terceros/mis-pedidos");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching pedidos:", error);
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleOpenUpdateDialog = (pedido) => {
    setSelectedPedido(pedido);
    setNuevoEstado(pedido.estado);
    setNotasActualizacion("");
    setUpdateDialogOpen(true);
  };

  const handleUpdateEstado = async () => {
    if (!selectedPedido || !nuevoEstado) return;
    
    setSubmitting(true);
    try {
      await api.put(`/pedidos-terceros/${selectedPedido.id}/estado`, {
        estado: nuevoEstado,
        notas_actualizacion: notasActualizacion || null
      });
      
      toast.success("Estado actualizado");
      setUpdateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPedidos = data?.pedidos?.filter(p => 
    filtroEstado === "todos" || p.estado === filtroEstado
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-pedidos">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Mis Pedidos</h1>
          <p className="text-torta-muted">Gestiona tus pedidos a terceros</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div 
          onClick={() => setFiltroEstado("todos")}
          className={`rounded-xl p-4 cursor-pointer transition-all ${
            filtroEstado === "todos" 
              ? "bg-torta-brown text-white ring-2 ring-torta-brown" 
              : "bg-white shadow-sm hover:shadow-md"
          }`}
        >
          <p className={`text-xs ${filtroEstado === "todos" ? "text-white/70" : "text-torta-muted"}`}>Total</p>
          <p className="font-display text-2xl">{data?.resumen?.total || 0}</p>
        </div>
        {Object.entries(ESTADOS).map(([key, value]) => (
          <div
            key={key}
            onClick={() => setFiltroEstado(key)}
            className={`rounded-xl p-4 cursor-pointer transition-all ${
              filtroEstado === key 
                ? `${value.color} ring-2 ring-current` 
                : "bg-white shadow-sm hover:shadow-md"
            }`}
          >
            <p className={`text-xs ${filtroEstado === key ? "" : "text-torta-muted"}`}>{value.label}</p>
            <p className="font-display text-2xl">
              {data?.resumen?.[key === "en_transito" ? "en_transito" : key + "s"] || 
               data?.resumen?.[key] || 0}
            </p>
          </div>
        ))}
      </div>

      {/* Pedidos List */}
      {filteredPedidos.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center">
          <Package className="w-12 h-12 text-torta-muted mx-auto mb-4" />
          <p className="text-torta-dark font-medium">No hay pedidos</p>
          <p className="text-torta-muted text-sm">
            {filtroEstado === "todos" 
              ? "Crea tu primer pedido a tercero desde 'Nueva Venta'" 
              : `No hay pedidos ${ESTADOS[filtroEstado]?.label.toLowerCase()}`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPedidos.map((pedido) => {
            const estadoInfo = ESTADOS[pedido.estado] || ESTADOS.pendiente;
            const EstadoIcon = estadoInfo.icon;
            const isExpanded = expandedPedido === pedido.id;

            return (
              <div
                key={pedido.id}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                {/* Header */}
                <div 
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedPedido(isExpanded ? null : pedido.id)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${estadoInfo.color}`}>
                    <EstadoIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-torta-dark">{pedido.cliente_nombre}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${estadoInfo.color}`}>
                        {estadoInfo.label}
                      </span>
                      {pedido.tipo_recaudo === "pago_vendedor" ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 flex items-center gap-1">
                          <Wallet className="w-3 h-3" />
                          Pagado
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Contra entrega
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-torta-muted text-sm mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pedido.cliente_direccion}
                      </span>
                      {pedido.fecha_entrega && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <Calendar className="w-3 h-3" />
                          {new Date(pedido.fecha_entrega).toLocaleDateString("es-CO", { day: "numeric", month: "short" })} {pedido.hora_entrega}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg text-torta-dark">
                      {formatCurrency(pedido.total)}
                    </p>
                    <p className="text-torta-muted text-xs">
                      {formatDate(pedido.fecha)}
                    </p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-torta-muted" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-torta-muted" />
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-torta-sand">
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      {/* Items */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-torta-dark">Productos</p>
                        <div className="bg-torta-cream/50 rounded-xl p-3 space-y-2">
                          {pedido.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.cantidad}x {item.producto_nombre}</span>
                              <span className="text-torta-muted">{formatCurrency(item.subtotal)}</span>
                            </div>
                          ))}
                          {pedido.costo_domicilio > 0 && (
                            <div className="flex justify-between text-sm border-t border-torta-sand pt-2">
                              <span>Domicilio</span>
                              <span className="text-torta-muted">{formatCurrency(pedido.costo_domicilio)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm border-t border-torta-sand pt-2 font-medium">
                            <span>Total ({pedido.total_unidades} und)</span>
                            <span>{formatCurrency(pedido.total)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Contact & Details */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-torta-dark">Datos del cliente</p>
                        <div className="bg-torta-cream/50 rounded-xl p-3 space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-torta-muted" />
                            <a 
                              href={`tel:${pedido.cliente_telefono}`}
                              className="text-blue-600 hover:underline"
                            >
                              {pedido.cliente_telefono}
                            </a>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-torta-muted mt-0.5" />
                            <div>
                              <p>{pedido.cliente_direccion}</p>
                              {pedido.cliente_barrio && (
                                <p className="text-torta-muted">Barrio: {pedido.cliente_barrio}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Fecha y hora de entrega */}
                          {pedido.fecha_entrega && (
                            <div className="flex items-center gap-2 pt-2 border-t border-torta-sand">
                              <Calendar className="w-4 h-4 text-blue-500" />
                              <span className="text-blue-700 font-medium">
                                Entrega: {new Date(pedido.fecha_entrega).toLocaleDateString("es-CO", { weekday: "short", day: "numeric", month: "short" })} a las {pedido.hora_entrega}
                              </span>
                            </div>
                          )}
                          
                          {/* Tipo de recaudo */}
                          <div className="flex items-center gap-2 pt-2 border-t border-torta-sand">
                            {pedido.tipo_recaudo === "pago_vendedor" ? (
                              <>
                                <Wallet className="w-4 h-4 text-green-600" />
                                <span className="text-green-700 font-medium">Pago a vendedor (Ya pagado)</span>
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 text-amber-600" />
                                <span className="text-amber-700 font-medium">Pago contra entrega ({pedido.metodo_pago})</span>
                              </>
                            )}
                          </div>
                          
                          {pedido.notas && (
                            <div className="pt-2 border-t border-torta-sand">
                              <p className="text-torta-muted">Notas: {pedido.notas}</p>
                            </div>
                          )}
                        </div>

                        {/* Info: El estado se actualiza desde Casa Matriz */}
                        {pedido.estado !== "entregado" && pedido.estado !== "cancelado" && (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-2">
                            <p className="text-blue-700 text-xs">
                              El estado del pedido será actualizado por Casa Matriz cuando sea procesado y entregado.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* History */}
                    {pedido.historial_estados?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-torta-sand">
                        <p className="text-sm font-medium text-torta-dark mb-2">Historial</p>
                        <div className="space-y-2">
                          {pedido.historial_estados.map((h, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs text-torta-muted">
                              <div className={`w-2 h-2 rounded-full ${ESTADOS[h.estado]?.color.split(' ')[0] || 'bg-gray-200'}`} />
                              <span>{formatDate(h.fecha)}</span>
                              <span className="capitalize">{ESTADOS[h.estado]?.label || h.estado}</span>
                              {h.notas && <span>- {h.notas}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Update Status Modal */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Actualizar Estado</DialogTitle>
          </DialogHeader>

          {selectedPedido && (
            <div className="space-y-4">
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="font-medium text-torta-dark">{selectedPedido.cliente_nombre}</p>
                <p className="text-sm text-torta-muted">{formatCurrency(selectedPedido.total)}</p>
              </div>

              <div className="space-y-2">
                <Label>Nuevo Estado</Label>
                <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="pagado_pendiente_entrega">Pagado - Por entregar</SelectItem>
                    <SelectItem value="confirmado">Confirmado</SelectItem>
                    <SelectItem value="en_transito">En Tránsito</SelectItem>
                    <SelectItem value="entregado">Entregado</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={notasActualizacion}
                  onChange={(e) => setNotasActualizacion(e.target.value)}
                  placeholder="Observaciones..."
                  rows={2}
                />
              </div>

              {nuevoEstado === "cancelado" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">
                    Al cancelar, el inventario será devuelto automáticamente.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setUpdateDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleUpdateEstado}
                  disabled={submitting || nuevoEstado === selectedPedido.estado}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {submitting ? "Actualizando..." : "Confirmar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
